import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useOptionalUserRoleContext } from '@/context/UserRoleContext';
import { withRetry } from '@/lib/supabaseRetry';

// Global cache to prevent duplicate requests
const roleCache = new Map<string, any>();
const pendingRequests = new Map<string, Promise<any>>();

export const useOptimizedUserRole = () => {
  // Try to use context first for deduplication
  const contextRole = useOptionalUserRoleContext();
  
  // If context is available, use it directly
  if (contextRole) {
    return contextRole;
  }
  
  // Fallback to standalone behavior (for components outside provider)
  return useStandaloneOptimizedUserRole();
};

// Standalone hook for use outside UserRoleProvider
const useStandaloneOptimizedUserRole = () => {
  const { user } = useAuth();
  const [state, setState] = useState({
    isAdmin: false,
    isAttorney: false,
    isClient: false,
    isDelegate: false,
    isInHouseCounsel: false,
    loading: true,
    lawyerData: null,
    lawFirmData: null,
    delegateData: null,
    inHouseCounselData: null,
  });
  
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!user) {
      setState({
        isAdmin: false,
        isAttorney: false,
        isClient: false,
        isDelegate: false,
        isInHouseCounsel: false,
        loading: false,
        lawyerData: null,
        lawFirmData: null,
        delegateData: null,
        inHouseCounselData: null,
      });
      return;
    }

    const cacheKey = `user_role_${user.id}`;

    // Allow a one-time bypass of the role cache after key transitions (e.g., invitation acceptance)
    // by navigating to any page with ?forceRoleRefresh=1.
    const forceRoleRefresh = new URLSearchParams(window.location.search).get('forceRoleRefresh') === '1';
    if (forceRoleRefresh) {
      roleCache.delete(cacheKey);
      pendingRequests.delete(cacheKey);
      // Best-effort: clean the URL so refresh doesn't repeat.
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('forceRoleRefresh');
        window.history.replaceState({}, '', url.toString());
      } catch {
        // ignore
      }
    }
    
    // Check cache first
    if (!forceRoleRefresh && roleCache.has(cacheKey)) {
      const cached = roleCache.get(cacheKey);
      if (mounted.current) {
        setState({ ...cached, loading: false });
      }
      return;
    }

    // Check for pending request
    if (pendingRequests.has(cacheKey)) {
      pendingRequests.get(cacheKey)?.then((result) => {
        if (mounted.current) {
          setState({ ...result, loading: false });
        }
      });
      return;
    }

    // Create new request with parallel fetching
    const fetchRoleData = async () => {
      try {
        console.log('[useOptimizedUserRole] 🚀 Fetching role data in parallel for user:', user.id);

        // Fetch user roles, lawyer info, delegate info, and in-house counsel info in parallel with retry
        const [rolesResult, lawyerResult, delegateResult, inHouseResult] = await Promise.allSettled([
          withRetry(() =>
            supabase
              .from('user_roles')
              .select('role')
              .eq('user_id', user.id)
          ),
          withRetry(() =>
            supabase
              .from('lawyers')
              .select(`
                *,
                firms:firm_id (
                  id,
                  name,
                  is_active
                )
              `)
              .eq('user_id', user.id)
              .eq('is_active', true)
              .maybeSingle()
          ),
          withRetry(() =>
            supabase
              .from('client_delegates')
              .select(`
                *,
                clients:client_id (
                  id,
                  first_name,
                  last_name,
                  company,
                  user_id
                ),
                firms:firm_id (
                  id,
                  name,
                  is_active
                )
              `)
              .or(`user_id.eq.${user.id},email.eq.${user.email?.toLowerCase()}`)
              .eq('is_active', true)
              .maybeSingle()
          ),
          withRetry(() =>
            supabase
              .from('in_house_counsel')
              .select('*')
              .eq('user_id', user.id)
              .eq('is_active', true)
              .maybeSingle()
          )
        ]);

        const roles = rolesResult.status === 'fulfilled' ? rolesResult.value.data : [];
        const lawyerInfo = lawyerResult.status === 'fulfilled' ? lawyerResult.value.data : null;
        const lawyerError = lawyerResult.status === 'fulfilled' ? lawyerResult.value.error : null;
        const delegateInfo = delegateResult.status === 'fulfilled' ? delegateResult.value.data : null;
        const inHouseInfo = inHouseResult.status === 'fulfilled' ? inHouseResult.value.data : null;

        const userRoles = roles?.map((r: any) => r.role) || [];
        const hasActiveLawyer = !lawyerError && !!lawyerInfo;
        const hasActiveDelegate = !!delegateInfo;
        const hasActiveInHouse = !!inHouseInfo;
        
        const isAdmin = userRoles.includes('admin');
        let isAttorney =
          hasActiveLawyer ||
          userRoles.includes('lawyer') ||
          userRoles.includes('law_firm_admin') ||
          userRoles.includes('law_firm_member');
        // Guard against stale role rows: if the user has no active lawyer record,
        // an admin should resolve as admin (not attorney) instead of hanging on
        // the attorney portal where there is no lawyer profile to render.
        if (isAdmin && !hasActiveLawyer) {
          isAttorney = false;
        }
        const isInHouseCounsel = hasActiveInHouse || userRoles.includes('in_house_counsel');
        const isDelegate = hasActiveDelegate && !isAdmin && !isAttorney && !isInHouseCounsel;
        const isClient = !isAdmin && !isAttorney && !isInHouseCounsel && !isDelegate;

        const result = {
          isAdmin,
          isAttorney,
          isClient,
          isDelegate,
          isInHouseCounsel,
          lawyerData: hasActiveLawyer ? lawyerInfo : null,
          lawFirmData: hasActiveLawyer && lawyerInfo?.firms ? lawyerInfo.firms : null,
          delegateData: hasActiveDelegate ? delegateInfo : null,
          inHouseCounselData: hasActiveInHouse ? inHouseInfo : null,
        };

        // Cache the result for 5 minutes
        roleCache.set(cacheKey, result);
        setTimeout(() => roleCache.delete(cacheKey), 5 * 60 * 1000);

        return result;
      } catch (error) {
        console.error('[useOptimizedUserRole] ❌ Error fetching role data:', error);
        return {
          isAdmin: false,
          isAttorney: false,
          isClient: false,
          isDelegate: false,
          isInHouseCounsel: false,
          lawyerData: null,
          lawFirmData: null,
          delegateData: null,
          inHouseCounselData: null,
        };
      }
    };

    const requestPromise = fetchRoleData();
    pendingRequests.set(cacheKey, requestPromise);

    requestPromise.then((result) => {
      pendingRequests.delete(cacheKey);
      if (mounted.current) {
        setState({ ...result, loading: false });
      }
    });
  }, [user?.id]);

  return {
    ...state,
    hasAttorneyAccess: () => state.isAdmin || state.isAttorney,
    hasClientAccess: () => !user || state.isClient || state.isDelegate || (!state.isAttorney && !state.isAdmin && !state.isInHouseCounsel),
    isExistingLawyer: () => state.lawyerData && state.lawyerData.is_active,
    hasActiveLawFirm: () => state.lawFirmData && state.lawFirmData.is_active,
    isTrialExpired: () => false,
  };
};